import { getSpinWheelTemplate } from "../../shortcodes/templates";
import { getShortcodeWrapper, addShortcodeToEditor } from "../../shortodeAddition";
import { parseToHtml } from "@/utility/parseToHtml";
import { SPIN_WHEEL_EVENT_TYPES } from "@/constant";
import { serializeDynamicComponents, visualizeDynamicComponents } from "@/assets/utilities/tinymce/visualization";
import { convertAssetsPathToRelativeLinks } from "@/utility/convertAssetsPaths";

function serializeContent(obj) {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => serializeContent(item));
  } else {
    const newObj = {};
    Object.keys(obj).forEach(key => {
      if (key === "message") {
        const content = parseToHtml(obj[key]);
        serializeDynamicComponents(content);
        newObj[key] = content.body.innerHTML;
      } else {
        newObj[key] = serializeContent(obj[key]);
      }
    });

    return newObj;
  }
}

function visualizeContent(obj) {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => visualizeContent(item));
  } else {
    const newObj = {};
    Object.keys(obj).forEach(key => {
      if (key === "message") {
        const content = parseToHtml(obj[key]);
        visualizeDynamicComponents(content);
        newObj[key] = content.body.innerHTML;
      } else {
        newObj[key] = visualizeContent(obj[key]);
      }
    });

    return newObj;
  }
}

const decodeFromSpinWheelData = element => {
  if (!element) {
    return {};
  }

  try {
    // Use same decoding pattern as live chat: decodeURIComponent(escape(window.atob(...)))
    const parsedData = JSON.parse(decodeURIComponent(escape(window.atob(element))));
    return parsedData;
  } catch (error) {
    console.error("Error parsing spin wheel data:", error, "Raw data:", element);
    // Return default empty data structure on parse error
    return {
      buttonText: "",
      buttonLink: "",
      message: "",
      redirectOnClick: false,
    };
  }
};

const editSpinWheelConfig = (editor, shortcodeWrapper) => {
  let currentData = decodeFromSpinWheelData(shortcodeWrapper.getAttribute("data-spin-wheel-data"));
  // Ensure currentData is always an object
  if (!currentData || typeof currentData !== "object") {
    currentData = {
      buttonText: "",
      buttonLink: "",
      message: "",
      redirectOnClick: false,
    };
  }

  const spinWheelWindow = editor.windowManager.openUrl({
    title: "Spin Wheel",
    url: "/spin-wheel-dialog",
    buttons: [
      {
        type: "cancel",
        name: "close",
        text: "Cancel",
      },
      {
        type: "custom",
        name: "submit",
        text: "Submit",
        primary: true,
      },
    ],
    onAction: function (instance) {
      // Ensure currentData is always an object (not null/undefined)
      if (!currentData) {
        currentData = {
          buttonText: "",
          buttonLink: "",
          message: "",
          redirectOnClick: false,
        };
      }

      // Save the data - use same encoding pattern as live chat
      try {
        // Convert message content to relative links if needed
        if (currentData.message) {
          currentData.message =
            convertAssetsPathToRelativeLinks(currentData.message)?.body?.innerHTML || currentData.message;
        }

        const serializedData = serializeContent(currentData);
        const encodedData = btoa(unescape(encodeURIComponent(JSON.stringify(serializedData))));
        shortcodeWrapper.setAttribute("data-spin-wheel-data", encodedData);
        editor.fire("change");
        editor.windowManager.alert("Success!");
        instance.close();
      } catch (error) {
        console.error("Error encoding spin wheel data:", error);
        editor.windowManager.alert("Error saving data. Please try again.");
      }
    },
    onMessage: (instance, message) => {
      if (message.mceAction === SPIN_WHEEL_EVENT_TYPES.REQUEST_DATA) {
        sendData();
      }

      if (message.mceAction === SPIN_WHEEL_EVENT_TYPES.UPDATE_SPIN_WHEEL_DATA && message.data) {
        currentData = message.data;
      }
    },
    width: 600,
    height: 800,
  });

  const sendData = () => {
    const spinWheelShortcodeData = shortcodeWrapper.getAttribute("data-spin-wheel-data");
    try {
      const parsedData = JSON.parse(decodeURIComponent(escape(window.atob(spinWheelShortcodeData))));
      const content = visualizeContent(parsedData);
      spinWheelWindow.sendMessage({
        type: SPIN_WHEEL_EVENT_TYPES.SEND_DATA,
        data: content,
      });
    } catch (error) {
      console.error("Error decoding spin wheel data:", error);
      // Send default empty data on error
      spinWheelWindow.sendMessage({
        type: SPIN_WHEEL_EVENT_TYPES.SEND_DATA,
        data: {
          buttonText: "",
          buttonLink: "",
          message: "",
          redirectOnClick: false,
        },
      });
    }
  };
};

const spinWheelConfig = (tinymce, uuid, shortcodeOnClick) => {
  let currentData = {
    buttonText: "",
    buttonLink: "",
    message: "",
    redirectOnClick: false,
  };

  const spinWheelWindow = tinymce.activeEditor.windowManager.openUrl({
    title: "Spin Wheel",
    url: "/spin-wheel-dialog",
    buttons: [
      {
        type: "cancel",
        name: "close",
        text: "Cancel",
      },
      {
        type: "custom",
        name: "submit",
        text: "Submit",
        primary: true,
      },
    ],
    onAction: function (instance) {
      // Ensure currentData is always an object (not null/undefined)
      if (!currentData) {
        currentData = {
          buttonText: "",
          buttonLink: "",
          message: "",
          redirectOnClick: false,
        };
      }

      // Save the data - use same encoding pattern as live chat
      try {
        // Convert message content to relative links if needed
        if (currentData.message) {
          currentData.message =
            convertAssetsPathToRelativeLinks(currentData.message)?.body?.innerHTML || currentData.message;
        }

        const shortcodeWrapper = getShortcodeWrapper(uuid, shortcodeOnClick, getSpinWheelTemplate(), "spin-wheel");
        const shortcodeWrapperEl = parseToHtml(shortcodeWrapper).querySelector(".shortcode");
        const serializedData = serializeContent(currentData);
        const encodedData = btoa(unescape(encodeURIComponent(JSON.stringify(serializedData))));
        shortcodeWrapperEl.setAttribute("data-spin-wheel-data", encodedData);
        addShortcodeToEditor(shortcodeWrapperEl.outerHTML, uuid);
        instance.close();
      } catch (error) {
        console.error("Error encoding spin wheel data:", error);
        tinymce.activeEditor.windowManager.alert("Error saving data. Please try again.");
      }
    },
    onMessage: (instance, message) => {
      if (message.mceAction === SPIN_WHEEL_EVENT_TYPES.UPDATE_SPIN_WHEEL_DATA) {
        currentData = message.data;
      }
    },
    width: 600,
    height: 800,
  });
};

export { spinWheelConfig, editSpinWheelConfig };
